(function (root) {
  'use strict';

  var ACTIVATION_TAG = 'zug_sample:true';
  var configPromises = {};

  function normalizeTags(tags) {
    if (Array.isArray(tags)) {
      return tags.map(function (tag) { return String(tag || '').trim().toLowerCase(); });
    }
    if (typeof tags === 'string') {
      return tags.split(/\|\||,/).map(function (tag) { return tag.trim().toLowerCase(); });
    }
    return [];
  }

  function hasTag(tags, expected) {
    var normalizedExpected = String(expected || '').trim().toLowerCase();
    return normalizeTags(tags).indexOf(normalizedExpected) !== -1;
  }

  function isActive(tags) {
    return hasTag(tags, ACTIVATION_TAG);
  }

  function load(url) {
    if (!url) return Promise.reject(new Error('Sample pricing URL is required.'));
    if (!configPromises[url]) {
      configPromises[url] = fetch(url, { credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error('Sample pricing request failed: HTTP ' + response.status);
          return response.json();
        })
        .then(function (config) {
          if (!config || config.schema_version !== 'sample_mode_v1' || !config.offers) {
            throw new Error('Invalid Sample Mode pricing contract.');
          }
          return config;
        });
    }
    return configPromises[url];
  }

  function resolve(config, tags, controller) {
    if (!isActive(tags) || !config || !config.offers) return null;
    var normalizedTags = normalizeTags(tags);
    var matches = Object.keys(config.offers).filter(function (key) {
      var offer = config.offers[key];
      if (!offer || offer.controller !== controller || offer.blocked_reason) return false;
      var required = offer.activation_tags || [];
      return required.length > 0 && required.every(function (tag) {
        return normalizedTags.indexOf(String(tag).toLowerCase()) !== -1;
      });
    });
    if (matches.length !== 1) return null;
    var resolved = Object.assign({ key: matches[0] }, config.offers[matches[0]]);
    return validateOffer(resolved) ? resolved : null;
  }

  function prepare(url, tags, controller) {
    if (!isActive(tags)) return Promise.resolve(null);
    return load(url).then(function (config) {
      return resolve(config, tags, controller);
    });
  }

  function validateOffer(offer) {
    if (!offer || !offer.label || !Number.isInteger(offer.quantity) || offer.quantity < 1) return false;
    if (!Number.isInteger(offer.price_cents) || offer.price_cents < 1) return false;
    if ((offer.width == null) !== (offer.height == null)) return false;
    if (offer.width != null && (!Number.isFinite(offer.width) || !Number.isFinite(offer.height) ||
        offer.width <= 0 || offer.height <= 0)) return false;
    return true;
  }

  function exactPrice(offer) {
    if (!validateOffer(offer)) return null;
    return {
      total: offer.price_cents / 100,
      unitPrice: offer.price_cents / 100 / offer.quantity,
      quantity: offer.quantity,
      qty: offer.quantity,
      sample: true
    };
  }

  function products2Pricing(offer, materialKey) {
    var pricing = exactPrice(offer);
    if (!pricing || offer.width == null || offer.height == null) return null;
    var width = Number(offer.width);
    var height = Number(offer.height);
    var quantity = Number(offer.quantity);
    var unit = pricing.total / quantity;
    pricing.areaSqIn = width * height;
    pricing.v2 = {
      version: 'sample_mode_v1',
      pricing_table_version: 'zug-sample-pricing.json',
      cart_mode: 'product_single',
      width_in: width,
      height_in: height,
      area_in2: width * height,
      size_tier_in2: width * height,
      qty_entered: quantity,
      q_low: quantity,
      q_high: quantity,
      u_low: unit,
      u_high: unit,
      u_interp: unit,
      u_final: unit,
      global_base_scalar: 1,
      material_key: materialKey,
      material_mult: 1,
      rush_option: 'regular',
      rush_mult: 1,
      total_desired: pricing.total,
      unit_price: unit,
      quantity: quantity
    };
    return pricing;
  }

  function sameValue(actual, expected) {
    if (actual == null || expected == null) return false;
    var actualNumber = Number(actual);
    var expectedNumber = Number(expected);
    if (Number.isFinite(actualNumber) && Number.isFinite(expectedNumber)) {
      return Math.abs(actualNumber - expectedNumber) < 0.000001;
    }
    return String(actual) === String(expected);
  }

  function matchesControl(control, group) {
    if (group.attribute && group.value != null &&
        sameValue(control.getAttribute(group.attribute), group.value)) return true;
    var match = group.matchAttributes;
    if (!match) return false;
    return Object.keys(match).every(function (attribute) {
      return sameValue(control.getAttribute(attribute), match[attribute]);
    });
  }

  function ensureControl(rootElement, group) {
    var controls = Array.prototype.slice.call(rootElement.querySelectorAll(group.selector));
    var selected = controls.filter(function (control) {
      return matchesControl(control, group);
    })[0];
    if (selected || !group.ensure) return controls;

    var container = rootElement.querySelector(group.containerSelector);
    if (!container) return controls;
    selected = document.createElement('button');
    selected.type = 'button';
    selected.className = group.ensure.className;
    selected.setAttribute(group.attribute, String(group.value));
    selected.textContent = group.ensure.label;
    var custom = group.ensure.beforeAttribute
      ? container.querySelector('[' + group.attribute + '="' + group.ensure.beforeAttribute + '"]')
      : null;
    container.insertBefore(selected, custom);
    return Array.prototype.slice.call(rootElement.querySelectorAll(group.selector));
  }

  function lockControls(rootElement, groups) {
    groups.forEach(function (group) {
      var controls = ensureControl(rootElement, group);
      controls.forEach(function (control) {
        var selected = matchesControl(control, group);
        if (group.activeClass) control.classList.toggle(group.activeClass, selected);
        control.disabled = !selected;
        control.setAttribute('aria-disabled', selected ? 'false' : 'true');
        control.setAttribute('data-zug-sample-locked-control', 'true');
        control.classList.toggle('zug-sample-control--disabled', !selected);
      });
    });
  }

  function hideControls(rootElement, selectors) {
    selectors.forEach(function (selector) {
      var element = rootElement.querySelector(selector);
      if (!element) return;
      element.style.display = 'none';
      element.setAttribute('aria-hidden', 'true');
      Array.prototype.forEach.call(element.querySelectorAll('button, input, select, textarea'), function (control) {
        control.disabled = true;
      });
    });
  }

  function lockStandaloneControl(rootElement, selector) {
    var control = rootElement.querySelector(selector);
    if (!control) return;
    control.disabled = true;
    control.setAttribute('aria-disabled', 'true');
    control.setAttribute('data-zug-sample-locked-control', 'true');
  }

  function selector(id, sectionId, child) {
    return '#' + id + (sectionId ? '-' + sectionId : '') + (child || '');
  }

  function lockFamilyUI(options) {
    var rootElement = options.root;
    var state = options.state;
    var offer = options.offer;
    var sectionId = options.sectionId || '';
    var groups = [];
    var hidden = [];

    if (!rootElement || !state || !validateOffer(offer)) return false;

    if (options.family === 'products_2_0') {
      groups = [
        { selector: selector('shape-buttons', sectionId, ' [data-shape]'), attribute: 'data-shape',
          value: state.shape, activeClass: 'sticker-calc__icon-btn--active' },
        { selector: selector('size-buttons', sectionId, ' [data-size]'), attribute: 'data-size',
          matchAttributes: { 'data-width': offer.width, 'data-height': offer.height },
          activeClass: 'sticker-calc__btn--active' },
        { selector: selector('quantity-buttons', sectionId, ' [data-qty]'), containerSelector: selector('quantity-buttons', sectionId),
          attribute: 'data-qty', value: state.quantityKey, activeClass: 'sticker-calc__btn--active',
          ensure: { className: 'sticker-calc__btn', label: String(offer.quantity), beforeAttribute: 'custom' } },
        { selector: selector('rush-buttons', sectionId, ' [data-rush]'), attribute: 'data-rush',
          value: 'regular', activeClass: 'sticker-calc__btn--active' },
        { selector: selector('lamination-buttons', sectionId, ' [data-lamination]'), attribute: 'data-lamination',
          value: state.lamination, activeClass: 'sticker-calc__btn--active' },
        { selector: selector('printing-buttons', sectionId, ' [data-printing]'), attribute: 'data-printing',
          value: state.coasterDoubleSided ? 'double' : 'single', activeClass: 'sticker-calc__icon-btn--active' }
      ];
      hidden = [
        selector('toggle-pricing', sectionId),
        selector('pricing-list', sectionId),
        selector('custom-size', sectionId),
        selector('custom-qty', sectionId),
        selector('easy-peel-row', sectionId)
      ];
    } else if (options.family === 'acrylic_v2') {
      groups = [
        { selector: selector('acr-shape-buttons', sectionId, ' [data-shape]'), attribute: 'data-shape',
          value: state.shape, activeClass: 'acr-calc__icon-btn--active' },
        { selector: selector('acr-size-buttons', sectionId, ' [data-size]'), attribute: 'data-size',
          matchAttributes: { 'data-width': offer.width, 'data-height': offer.height },
          activeClass: 'acr-calc__btn--active' },
        { selector: selector('acr-quantity-buttons', sectionId, ' [data-qty]'), containerSelector: selector('acr-quantity-buttons', sectionId),
          attribute: 'data-qty', value: state.quantityKey, activeClass: 'acr-calc__btn--active',
          ensure: { className: 'acr-calc__btn', label: String(offer.quantity), beforeAttribute: 'custom' } },
        { selector: selector('acr-rush-buttons', sectionId, ' [data-rush]'), attribute: 'data-rush',
          value: 'regular', activeClass: 'acr-calc__btn--active' }
      ];
      hidden = [
        selector('acr-toggle-pricing', sectionId),
        selector('acr-pricing-list', sectionId),
        selector('acr-custom-size', sectionId),
        selector('acr-custom-qty', sectionId)
      ];
    } else if (options.family === 'air_freshener') {
      groups = [
        { selector: '#af-shape-buttons [data-shape]', attribute: 'data-shape',
          value: state.shape, activeClass: 'af-widget__icon-btn--active' },
        { selector: '#af-print-style-buttons [data-print-style]', attribute: 'data-print-style',
          value: state.printStyle, activeClass: 'af-widget__opt-btn--active' },
        { selector: '#af-qty-presets [data-qty]', containerSelector: '#af-qty-presets',
          attribute: 'data-qty', value: String(state.quantity), activeClass: 'af-widget__preset--active',
          ensure: { className: 'af-widget__preset', label: String(offer.quantity), beforeAttribute: 'custom' } }
      ];
      hidden = ['#af-toggle-pricing', '#af-pricing-list', '#af-custom-qty'];
      lockStandaloneControl(rootElement, '#af-fragrance-trigger');
    } else {
      return false;
    }

    Array.prototype.forEach.call(rootElement.querySelectorAll('.zug-sample-summary'), function (summary) {
      summary.remove();
    });
    lockControls(rootElement, groups);
    hideControls(rootElement, hidden);
    rootElement.classList.remove(options.pendingClass);
    rootElement.classList.add(options.activeClass);
    return true;
  }

  function applyLockedState(state, offer) {
    state.shape = offer.shape || state.shape;
    if (offer.width != null) {
      state.width = Number(offer.width);
      state.height = Number(offer.height);
      state.sizeKey = state.width + 'x' + state.height;
    }
    state.quantity = Number(offer.quantity);
    state.quantityKey = String(state.quantity);
    state.rushOption = 'regular';
    state.addEasyPeel = false;
    if (offer.material_label) state.materialLabel = offer.material_label;
    return state;
  }

  function applyProperties(properties, offer) {
    if (!properties || !validateOffer(offer)) return properties;
    properties['Product Type'] = 'Sample';
    properties['Sample Product'] = offer.label;
    properties['Sample Offer'] = 'Yes';
    properties['Pieces'] = String(offer.quantity);
    properties['_sample_mode'] = 'true';
    properties['_sample_key'] = offer.key;
    properties['_sample_price_cents'] = String(offer.price_cents);
    return properties;
  }

  root.ZUGSampleMode = {
    activationTag: 'ZUG_SAMPLE:true',
    normalizeTags: normalizeTags,
    isActive: isActive,
    load: load,
    prepare: prepare,
    resolve: resolve,
    validateOffer: validateOffer,
    exactPrice: exactPrice,
    products2Pricing: products2Pricing,
    lockFamilyUI: lockFamilyUI,
    applyLockedState: applyLockedState,
    applyProperties: applyProperties
  };
})(window);
